import { flow, Instance, isAlive, SnapshotIn, SnapshotOut, types } from 'mobx-state-tree';
import EditorError from '../../_shared/editor-error';
import {
  AddToCartError,
  BUSINESS_VALIDATION_CAUSE_ERRORS,
  ICart,
  SDK_CART_ERRORS,
  SdkBusinessValidationCauseErrorType,
  ErrorDiagnostics,
} from '@photosi/expo-photosi-sdk/build/Cart';
import { AddToCartStatusUpdate } from '@photosi/expo-photosi-sdk/build/Project';
import { PickedImage, ThumbnailSize } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { IProjectModel, ProjectModelInstance } from '../_models/project';
import { sdkProjects } from '../_shared/sdkUtils';
import { isWeb } from '../../_shared/utils/isWeb';
import { IProductModel, ProductModel, ProductModelInstance } from '../_models/product';
import { IUserImageAreaModel, UserImageAreaModelInstance } from '../_models/userImageArea';
import { GroupModelInstance, IGroupModel } from '../_models/group';
import { ACTIVE_TOOLS, EDITOR_VIEW_MODE, FINALIZE_ACTIVE_PRODUCT_DELAY } from './pageProject.constants';
import { getActiveElementBy, getSafeErrorCode } from './pageProject.utils';
import { allowExit } from '../../_shared/utils/prevent-exit/preventExit';
import Datadog from '../../_shared/datadog/index.web';
import { DATADOG_CUSTOM_ACTIONS } from '../../_shared/datadog/DatadogCustomActions';
import { captureConnectionContext } from '../../_shared/datadog/bootstrapContext';
import { markBootstrapStep, reportBootstrapFailure } from '../../_shared/datadog/bootstrap-steps/bootstrapSteps';
import { getNPhotosBucket } from 'src/_shared/utils/get-n-photos-bucket/getNPhotosBucket';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import braze from 'src/_shared/braze';
import { PreviewSvgModelInstance } from '../_models/previewSvg';
import { logError, logInfo, logSDKError, logWarning } from 'src/_shared/logger';
import { PreviewCanvasModelInstance } from '../_models/previewCanvas';
import { handleGlobalError } from 'src/_shared/utils/handle-global-error/handleGlobalError';
import { DYNAMIC_MODAL_VIEWS, SELECTED_MENU_ITEMS } from '../_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { BaseListenersModel } from '../_models/_base/baseListenersModel';
import { openWindow } from 'src/_shared/utils/open-window/openWindow';
import { PreviewPrintModelInstance } from '../_models/previewPrint';
import { TProjectOptions } from 'src/_shared/utils/project-options/projectOptions.types';
import { LookupCodes } from 'src/constants/lookup.constants';
import { parseAnalyticsParamsByUserImage, parseAnalyticsParamsByUserImageArea } from 'src/_shared/analytics.utils';
import { Dimensions } from 'react-native';
import { isHappyCaseOrPhotoKit } from 'src/helpers/group.helper';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { navigateTo, navigationResetTo } from 'src/editor-v3/helpers/navigation.helper';
import { STEPS } from 'src/editor-v3/components/upload-picker/UploadSteps';
import { getDefaultProjectOptions } from 'src/_shared/utils/project-options/getDefaultProjectOptions';
import { getProjectOptions } from 'src/_shared/utils/project-options/get-project-options/getProjectOptions.web';
import { goToLoginScreen } from 'src/_shared/utils/go-to-login-screen/goToLoginScreen';
import { isUploadRoute } from 'src/_shared/utils/is-new-editor/isNewEditor.web';
import { retryWithBackoff } from 'src/_shared/utils/retry-with-backoff';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';
import { extractErrorMessage, isProductNotAvailableError } from 'src/helpers/error.helper';
import { CHECKOUT_BASE_URL } from 'src/app/settings';
import { trackCartAddSuccessEvent } from 'src/_shared/utils/tracking-events/trackCartAddSuccessEvent';

export type TProjectPageModel = typeof PageProject;
export type ProjectPageModelInstance = Instance<TProjectPageModel>;
export interface IProjectPageModelIn extends SnapshotIn<TProjectPageModel> {}
export interface IProjectPageModelOut extends SnapshotOut<TProjectPageModel> {}
export interface IProjectPageModel extends ProjectPageModelInstance {}

export const DEFAULT_GROUP_COLUMNS = 3;
const NO_PROJECT_ID_PROVIDED = 'No projectId provided';
const NO_PRODUCT_CONFIGURATION_ID_PROVIDED = 'No productConfigurationId provided';
const NO_PROJECT_OR_GROUP_CODE_PROVIDED = 'No project or group code provided';

// Shared by addProductsV2/addProductsV3 (previously two copies of the same ad-hoc check).
function resolveGroupCodeOrThrow(
  self: { activeProject: ProjectModelInstance | undefined; activeGroupId?: string; groupCode?: string },
  groupCode?: string,
): string {
  const currentGroupCode = groupCode ?? self.activeGroupId ?? self.groupCode;
  if (!self.activeProject || !currentGroupCode) throw new Error(NO_PROJECT_OR_GROUP_CODE_PROVIDED);
  return currentGroupCode;
}

export const PageProject = BaseListenersModel.named('PageProject')
  .props({
    isNewEditor: types.optional(types.boolean, false),
    productFamily: types.optional(types.string, ''),

    // OLD FLOW
    productConfigurationId: types.maybe(types.string),

    // UTILS
    removingProduct: types.maybe(types.safeReference(ProductModel)),
    currentRoute: types.optional(types.enumeration(Object.values(EDITOR_VIEW_MODE)), EDITOR_VIEW_MODE.pictureView),
    activeTool: types.optional(types.enumeration(Object.values(ACTIVE_TOOLS)), ACTIVE_TOOLS.frame),
    isProductsScrollEnabled: types.optional(types.boolean, true),
    isProductSelectionEnabled: types.optional(types.boolean, true),
    isAddToCartDisabled: types.optional(types.boolean, false), // Default: pulsante abilitato
    areInteractionsEnabled: types.optional(types.boolean, true),
    isProductEditMode: types.optional(types.boolean, false), // for those products that have multiple editor layers
    activeLookup: types.optional(types.enumeration(Object.values(LookupCodes)), LookupCodes.EMPTY),
    isBottomSheetLookupOpened: types.optional(types.boolean, false),
    bottomSheetLookupHeight: types.optional(types.number, 0),
    showNotificationPhotoDrawer: types.optional(types.boolean, false),
    showAddProductMobile: types.optional(types.boolean, false),

    // NEW FLOW
    showUploadPhotosBanner: types.optional(types.boolean, true),
    showSliderZoomTutorial: types.optional(types.boolean, true),
    activeProjectId: types.maybe(types.string),
    activeProductId: types.maybe(types.string),
    nextActiveProductId: types.maybe(types.string),
    activeUserImageAreaId: types.maybe(types.string),
    shareTokenMobile: types.maybe(types.string),
    shareTokenFamilyAndFriends: types.maybe(types.string),

    // GROUPS
    groupCode: types.maybe(types.string), // it's the default group code, it is defined at sdk initialization
    activeGroupId: types.maybe(types.string), // it's the active group code, it could be undefined if no group is selected
  })
  .volatile<{
    switchToGroupHelper:
      | {
          groupId: string;
          scrollTo: 'top' | 'bottom' | number | IProductModel;
          forceOpening?: boolean;
        }
      | undefined;
    isFromSmartphoneORFamilyAndFriends: boolean;
    scrollOffset: number;
    isUploading: boolean;
    personalizedEventSent: boolean;
    selectUserImageToken: symbol | undefined;
  }>(self => ({
    switchToGroupHelper: undefined,
    isFromSmartphoneORFamilyAndFriends: false,
    scrollOffset: 0,
    isUploading: false,
    personalizedEventSent: false,
    selectUserImageToken: undefined,
  }))
  .views(self => ({
    get activeProject(): ProjectModelInstance | undefined {
      return self.root.data.projects.find(project => project.id === self.activeProjectId);
    },
  }))
  .views(self => ({
    get activeGroup(): GroupModelInstance | undefined {
      return self.activeProject?.groups.find(group => group.id === self.activeGroupId);
    },
  }))
  .views(self => ({
    get activeProduct(): ProductModelInstance | undefined {
      return self.activeProject?.products.find(product => product.id === self.nextActiveProductId);
    },
  }))
  .views(self => ({
    get activePreview(): PreviewSvgModelInstance | PreviewCanvasModelInstance | PreviewPrintModelInstance | undefined {
      return self.activeProduct?.preview;
    },
  }))
  .views(self => ({
    get activeUserImageArea(): UserImageAreaModelInstance | undefined {
      if (self.activeProduct?.preview.isMultiphoto) {
        return self.activePreview?.userImageAreas.find(
          userImageArea => userImageArea.id === self.activeUserImageAreaId,
        );
      } else {
        return self.activeProduct?.preview?.userImageArea;
      }
    },
  }))
  .views(self => ({
    get totalNumberOfProducts() {
      return self.activeProject?.totalNumberOfProducts || 0;
    },
    get allProducts() {
      return self.activeProject?.products || [];
    },
    get activeProductIndex() {
      return self.activeProject?.products.findIndex(product => product.id === self.activeProduct?.id);
    },
    get isEditModeActive() {
      return self.currentRoute === EDITOR_VIEW_MODE.editor;
    },
    get isPreviewModeActive() {
      return self.currentRoute === EDITOR_VIEW_MODE.pictureView;
    },
    get isProductEditModeActive() {
      return self.isProductEditMode;
    },

    get isEmpty() {
      // todo - check if it works
      return self.activeProject?.isEmpty || false;
    },
    get cartId() {
      return self.activeProject?.cartId || null;
    },
    /**
     * il progetto è considerato a singolo prodotto nel caso di quadretti adesivi
     * viene utilizzato per nascondere i tasti di aggiungi prodotto
     */
    get isProductSetProject() {
      return !!self.activeProject?.isProductSetProject;
    },
  }))
  .views(self => ({
    get isProjectAddedToCart() {
      return !!self.cartId;
    },
    get isRemovingLastProduct() {
      return (
        self.totalNumberOfProducts > 0 &&
        (self.activeProject?.products.length ?? 0) >= self.totalNumberOfProducts &&
        self.removingProduct?.id === self.activeProject?.products[self.totalNumberOfProducts - 1].id
      );
    },
  }))
  .actions(self => {
    return {
      applyEnableAddToCart() {
        self.isAddToCartDisabled = false;
        // Chiude la metrica add_to_cart_ready_time se è in corso (cioè se è
        // appena terminato un photo_picker_to_first_image_time). No-op altrimenti.
        self.root.data.endAddToCartReadyMeasure();
      },
      finalizeActiveProduct(newActiveProductId: string | undefined) {
        self.activeProductId = newActiveProductId;
        self.activeUserImageAreaId = undefined;
      },
      enableInteractions() {
        self.areInteractionsEnabled = true;
      },
      disableInteractions() {
        self.areInteractionsEnabled = false;
      },
      setSelectUserImageToken(value: symbol | undefined) {
        self.selectUserImageToken = value;
      },
    };
  })
  .actions(self => {
    //timer to work with FINALIZE_ACTIVE_PRODUCT_DELAY variable.
    let activeProductTimeout: NodeJS.Timeout | null = null;
    //timer to handle add-to-card button debouncer
    let enableButtonTimeout: NodeJS.Timeout | null = null;
    return {
      // deactivates add to cart button
      disableAddToCart() {
        if (enableButtonTimeout) {
          clearTimeout(enableButtonTimeout); // Prevents premature reactivation
          enableButtonTimeout = null;
        }
        self.isAddToCartDisabled = true;
      },

      // Debounced reactivation of the "Add to Cart" button
      enableAddToCart: flow(function* enableAddToCart() {
        if (enableButtonTimeout) {
          clearTimeout(enableButtonTimeout);
        }

        enableButtonTimeout = setTimeout(() => {
          self.applyEnableAddToCart(); //uses separated action because inside setTimeout
        }, 1000);
      }),

      setActiveGroup(group: IGroupModel) {
        self.activeProject?.deselectAllProducts();
        self.activeGroupId = group.id;
      },

      clearActiveGroup() {
        self.activeProject?.deselectAllProducts();
        self.activeGroupId = undefined;
      },

      setActiveGroupId(groupId: string) {
        self.activeProject?.deselectAllProducts();
        self.activeGroupId = groupId;
      },

      setActiveProject(project: IProjectModel | undefined) {
        self.activeProject?.deselectAllProducts();

        self.activeProjectId = project?.id ?? undefined;
        self.activeGroupId = undefined;
        self.activeProductId = undefined;
        self.activeUserImageAreaId = undefined;
      },

      setActiveProduct(prop: number | string | IProductModel | undefined, force?: boolean) {
        const newActiveProduct = getActiveElementBy<IProductModel>(prop, self.activeProject?.products);
        const newActiveProductId = newActiveProduct?.id ?? undefined;

        // If productId is undefined or force is true, finalize immediately
        if (!newActiveProductId || force) {
          self.finalizeActiveProduct(undefined);
          return;
        }

        // nextActiveProductId serves as a shield to avoid unwanted activeProduct updates when the
        // user scrolls fast over multiple products
        if (newActiveProductId !== self.nextActiveProductId) {
          self.nextActiveProductId = newActiveProductId;

          //the timeout is cleared every time setActiveProduct is called.
          if (activeProductTimeout) {
            clearTimeout(activeProductTimeout);
          }

          // Store a reference to `self`
          const store = self;

          activeProductTimeout = setTimeout(() => {
            store.finalizeActiveProduct(newActiveProductId);
          }, FINALIZE_ACTIVE_PRODUCT_DELAY);
        }
      },

      setActiveUserImageArea(prop?: number | string | IUserImageAreaModel | undefined) {
        const newActiveUserImageArea = getActiveElementBy<IUserImageAreaModel>(
          prop,
          self.activePreview?.userImageAreas,
        );

        if (newActiveUserImageArea !== self.activeUserImageArea) {
          self.activeUserImageAreaId = newActiveUserImageArea?.id;
        }
      },

      setProductConfigurationId(id: string | undefined) {
        self.productConfigurationId = id;
      },

      setProductFamily(productFamily: string) {
        self.productFamily = productFamily;
      },

      setGroupCode(groupCode?: string) {
        self.groupCode = groupCode;
      },

      setCurrentRoute(route: EDITOR_VIEW_MODE) {
        self.currentRoute = route;
      },

      setShowUploadPhotosBanner(show: boolean) {
        self.showUploadPhotosBanner = show;
      },

      setUploading(value: boolean) {
        self.isUploading = value;
      },

      setShowAddProductMobile(show: boolean) {
        self.showAddProductMobile = show;
      },

      setShowSliderZoomTutorial(show: boolean) {
        self.showSliderZoomTutorial = show;
      },

      setActiveTool(activeTool: ACTIVE_TOOLS) {
        self.activeTool = activeTool;
      },

      setActiveLoopkup(activeTool: LookupCodes) {
        self.activeLookup = activeTool;
      },

      setBottomSheetLookupOpened(value: boolean) {
        self.isBottomSheetLookupOpened = value;
      },

      setScrollingEnabled(enabled: boolean) {
        self.isProductsScrollEnabled = enabled;
      },

      setIsProductSelectionEnabled(enabled: boolean) {
        self.isProductSelectionEnabled = enabled;
      },
      setIsProductEditMode(isProductEditMode: boolean) {
        self.isProductEditMode = isProductEditMode;
      },
      setShareTokenMobile(token: string) {
        self.shareTokenMobile = token;
      },
      setShareTokenFamilyAndFriends(token: string) {
        self.shareTokenFamilyAndFriends = token;
      },
      setBottomSheetLookupHeight(value: number) {
        self.bottomSheetLookupHeight = value;
      },
      setShowNotificationPhotoDrawer(value: boolean) {
        self.showNotificationPhotoDrawer = value;
      },
    };
  })
  .actions(self => {
    const goToConfigurationSteps = flow(function* goToConfigurationSteps({
      initialProductConfigurationId,
      groupCode,
      groupId,
      skipConfigurationSteps = [],
      fileIds,
    }) {
      self.root.dynamicModal.openModal({
        dynamicModalView:
          Dimensions.get('window').width >= 1280
            ? DYNAMIC_MODAL_VIEWS.ConfigurationSteps
            : DYNAMIC_MODAL_VIEWS.ConfigurationStepsMobile,
        initialProductConfigurationId,
        groupCode,
        skipConfigurationSteps,
        groupId,
      });
    });

    const openUploadMultiSourceModal = flow(function* openUploadMultiSourceModal({
      initialProductConfigurationId,
      groupCode,
      groupId,
      skipConfigurationSteps = [],
      fileIds,
    }) {
      self.root.dynamicModal.openModal({
        dynamicModalView: DYNAMIC_MODAL_VIEWS.UploadMultiSource,
        newUploadMultiSourceCallback: params => {
          self.startAddingPhotosFlow({
            initialProductConfigurationId,
            groupCode,
            groupId,
            skipConfigurationSteps,
            ...params,
          });
        },
      });
    });

    return {
      goToConfigurationSteps,
      openUploadMultiSourceModal,
    };
  })
  .actions(self => {
    let eventSubscriptions: any[] = [];
    // Listens to SDK events and applies the debounce mechanism
    const listenForCartEvents = () => {
      self.addListener('DisableAddToCart', () => {
        self.disableAddToCart();
      });

      self.addListener('EnableAddToCart', () => {
        self.enableAddToCart();
      });

      self.addListener('AddToCartStatusUpdate', (event: AddToCartStatusUpdate) => {
        console.log(`# eventListener(AddToCartStatusUpdate) - event: `, event);
        self.activeProject?.products.forEach(product => product.onCartUpdated(event));
      });
    };

    // Cleanup for event listeners
    const cleanupCartEvents = () => {
      eventSubscriptions.forEach(sub => sub.remove());
      eventSubscriptions = [];
    };

    const goBack = async () => {
      self.setSelectUserImageToken(undefined);
      self.enableInteractions();
      self.setActiveUserImageArea(undefined);
      if (self.isProductEditModeActive && self.isEditModeActive) {
        goToMultiphotoEditorPage();
      } else {
        goToPreviewPage();
      }
    };

    const goToPreviewPage = async () => {
      self.setIsProductEditMode(false);
      self.setCurrentRoute(EDITOR_VIEW_MODE.pictureView);
      await self.activePreview?.unloadAll();
    };

    const activateEditMode = flow(function* activateEditMode(activeTool?: ACTIVE_TOOLS, autosave = true) {
      amplitude.trackEvent(
        AnalyticsEvents.modifyPhoto.view,
        parseAnalyticsParamsByUserImageArea(self.activeUserImageArea),
      );
      braze.trackEvent(AnalyticsEvents.modifyPhoto.view);
      self.setCurrentRoute(EDITOR_VIEW_MODE.editor);
      self.setActiveTool(activeTool || ACTIVE_TOOLS.frame);
      yield self.activePreview?.unloadAll();
      yield self.activeUserImageArea?.userImage?.select(autosave);
    });

    // this action is used to go to the new editor layer, used for multiphoto
    const goToMultiphotoEditorPage = async () => {
      self.setCurrentRoute(EDITOR_VIEW_MODE.productEditor);
      self.setIsProductEditMode(true);

      await self.activePreview?.unloadAll();
      await self.activePreview?.selectAll();
    };

    const addToCart = flow(function* addToCart(newRoute = false) {
      if (!self.activeProject?.sdkProject) return;
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.add_to_cart_time);
      const filesPendingUpload =
        (self.activeProject.sdkProject as unknown as { getQueuedFilesCount?: () => number }).getQueuedFilesCount?.() ??
        0;
      const nFiles = self.activeProject.totalNumberOfUserImages ?? 0;
      const editorVersion = self.root.data.currentEditorVersion ?? 'unknown';
      const voucherifyFlag = braze.getBrazeFeatureFlag('voucherify')?.enabled;

      // Snapshot dei valori usati come payload sia nel success che nel failure metric.
      // Catturati al click (NON al completamento) per riflettere lo stato del carrello al momento del tentativo.
      const baseMetricPayload = {
        n_photos: nFiles,
        n_photos_bucket: getNPhotosBucket(nFiles),
        n_products: self.totalNumberOfProducts,
        files_pending_upload: filesPendingUpload,
        editor_version: editorVersion,
        ...captureConnectionContext(),
      };

      return handleGlobalError(
        self.activeProject?.sdkProject?.addToCart(voucherifyFlag).then((cart: ICart) => {
          return Promise.all([
            cart.getWebCartUrl().then(async href => {
              await amplitude.trackEvent(AnalyticsEvents.personalize.addToCartRedirect, {
                n_products: self.totalNumberOfProducts,
                n_photos: self.activeProject?.totalNumberOfUserImages ?? 0,
                n_photos_facebook: 0,
                n_photos_gphoto:
                  self.activeProject?.selectedThumbnails.filter(
                    thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.GooglePhoto,
                  )?.length ?? 0,
                n_photos_local:
                  self.activeProject?.selectedThumbnails.filter(
                    thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.Computer,
                  )?.length ?? 0,
                n_photos_smartphone:
                  self.activeProject?.selectedThumbnails.filter(
                    thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.Smartphone,
                  )?.length ?? 0,
                n_photos_family_friends:
                  self.activeProject?.selectedThumbnails.filter(
                    thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.FamilyFriends,
                  )?.length ?? 0,
              });
              braze.trackEvent(AnalyticsEvents.personalize.addToCartRedirect);

              if (__DEV__) {
                // in local development environment redirect to local checkout
                return href.replace(/^https:\/\/[^\/]*/, CHECKOUT_BASE_URL);
              } else {
                return href;
              }
            }),
            cart.getWebCartInfo(),
            cart.getCreatedCartRows(),
          ]).then(async ([href, cartInfo, createdCartRows]) => {
            allowExit();
            // workaround to clean browser history - start
            const params = new URLSearchParams(window.location.search);
            params.delete('projectId');
            const replaceUrl = params.size > 0 ? `/${newRoute ? 'editor-new' : ''}?${params.toString()}` : '/';
            history.replaceState({}, '', replaceUrl);
            Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.add_to_cart_time, {
              n_files: nFiles,
              files_pending_upload: filesPendingUpload,
              editor_version: editorVersion,
              ...captureConnectionContext(),
            });

            Datadog.logInfo(DATADOG_CUSTOM_ACTIONS.add_to_cart_success, baseMetricPayload); // One-shot success event. Combinato con add_to_cart_failure permette di calcolare il success rate su Datadog/Grafana

            await trackCartAddSuccessEvent(self.root.user, cartInfo, createdCartRows);

            // workaround to clean browser history - end
            await openWindow(href, { target: '_self' });
            return cart;
          });
        }),
      ).catch(error => {
        let errorType = '';
        let errorCauseCode: string | undefined;
        let errorString: string;
        let diagnostics: ErrorDiagnostics = {
          flow_step: 'unknown',
          total_files: null,
          is_resume: false,
          error_category: 'unknown',
          final_network_quality: 'unknown',
          initial_network_quality: 'unknown',
          user_id: null,
          total_bytes: null,
        };
        if (typeof error === 'string') {
          errorType = error;
          errorString = error;
        } else if (typeof error === 'object' && error !== null) {
          // Eventi DOM grezzi (es. xhr.onerror/worker.onerror passati a reject) non hanno
          // errorCode né message: li riconosciamo da isTrusted e li mappiamo su
          // DOM_EVENT_LEAK, coerentemente con classifyAddToCartError dell'SDK. Il fallback
          // a '' garantisce che errorType resti sempre una stringa (evita il crash di
          // errorType.match() più sotto quando errorCode/message sono entrambi assenti).
          errorType =
            error?.isTrusted === true ? SDK_CART_ERRORS.DOM_EVENT_LEAK : (error?.errorCode ?? error?.message ?? '');
          errorString = JSON.stringify(error);
          errorCauseCode = error?.cause?.errorCode;
          diagnostics = error.diagnostics ?? diagnostics;
        } else {
          errorType = '';
          errorString = '';
        }
        const isProductNotAvailable = isProductNotAvailableError(error);

        // Pulizia del measure aperto a inizio flow: non vogliamo lasciare uno start orfano
        // tra le performingActions del Datadog wrapper, e non vogliamo nemmeno emettere
        // add_to_cart_time per un attempt fallito (alterrebbe il P95 della metrica di durata).
        Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.add_to_cart_time);

        // OPERATION_IN_PROGRESS: l'SDK ha rilevato una seconda invocazione di addToCart
        // sulla stessa istanza mentre la prima è ancora in corso (es. doppio click,
        // race tra due tab). Vogliamo che parta SOLO la prima — il secondo invocation
        // ritorna immediatamente con questo errorCode. Nessun log, nessuna modale,
        // nessuna telemetria: la prima chiamata produrrà l'esito reale per conto suo.
        // String literal qui invece di SDK_CART_ERRORS.OPERATION_IN_PROGRESS per non
        // bloccare il merge sul release SDK che lo aggiunge al dizionario; quando la
        // versione SDK pubblicata includerà la costante, possiamo passare a quella.
        if (errorType === SDK_CART_ERRORS.OPERATION_IN_PROGRESS) {
          return;
        }

        if (!!errorType.match(SDK_CART_ERRORS.OPERATION_CANCELLED)) {
          // Cancellazione utente: NON è un fallimento del sistema. Loggata come info,
          // esclusa dal failure metric per non sporcare il rate di affidabilità.
          logInfo(
            `[addToCart]`,
            errorString,
            JSON.stringify({
              projectId: self.activeProject?.id,
              deviceInfo: self.deviceInfo,
            }),
          );
        } else {
          // Failure metric (esclusa la cancellazione utente sopra). Errore di sistema/SDK.
          const safeErrorCode = getSafeErrorCode({ errorType, errorCauseCode, isProductNotAvailable });

          Datadog.logInfo(DATADOG_CUSTOM_ACTIONS.add_to_cart_failure, {
            ...baseMetricPayload,
            error_code: safeErrorCode,
            ...diagnostics,
          });

          // Sentry context: kept intentionally minimal. The SDK already attaches the
          // useful aggregates (total_files, total_bytes, file_size_buckets, network
          // quality, retry counts) in `error.context` — see UploadFailureContext in
          // photosi-sdk-core. Adding the full `selectedThumbnails` array here only
          // bloated the Sentry message with per-file metadata (blob URLs, filenames,
          // dimensions, timestamps) that has unbounded cardinality and zero aggregate
          // diagnostic value. `voucherifyFlag` is dropped too: it's covered by the
          // Datadog failure metric branch above (which already breaks down by
          // error_code), and an A/B flag has no triage value on a single Sentry event.
          // `projectId` is preserved so an event can still be cross-referenced with
          // the project record in the admin tool when needed.
          const logContext = JSON.stringify({
            projectId: self.activeProject?.id,
            deviceInfo: self.deviceInfo,
          });

          // Codici che teniamo FUORI da Sentry: il loro volume è già coperto dalla
          // failure metric Datadog (sopra) e in Sentry sarebbero solo rumore non azionabile.
          // - IMAGES_NOT_AVAILABLE: condizione di business (immagini non disponibili o
          //   prodotto sotto il minimo di foto), già gestita lato UI dalla modale.
          // - DOM_EVENT_LEAK: un evento DOM (tipicamente errore di rete da xhr/worker)
          //   finito nel reject senza dettagli utili — in Sentry resterebbe solo {isTrusted:true}.
          // - BUSINESS_VALIDATION_CAUSE_ERRORS (PRODUCT_BELOW_MIN_PHOTOS / GROUP_LIMITS_VIOLATED):
          //   violazioni di regole di business attese, gestite a UI dalla modale. Restano
          //   tracciate su Datadog col loro codice reale (vedi sopra) per la dashboard dedicata.
          // - PRODUCT_NOT_AVAILABLE: prodotto disabilitato lato BE, gestito a UI dalla modale dedicata.
          // Le logghiamo come info (niente captureException) mantenendo modale e metrica.
          const isSilencedOnSentry =
            isProductNotAvailable ||
            (
              [SDK_CART_ERRORS.IMAGES_NOT_AVAILABLE, SDK_CART_ERRORS.DOM_EVENT_LEAK] as Array<
                AddToCartError['errorCode']
              >
            ).includes(errorType as AddToCartError['errorCode']) ||
            (errorCauseCode &&
              Object.values(BUSINESS_VALIDATION_CAUSE_ERRORS).includes(
                errorCauseCode as SdkBusinessValidationCauseErrorType,
              ));

          if (isSilencedOnSentry) {
            logInfo(`[addToCart]`, errorString, logContext);
          } else {
            logError(`[addToCart]`, errorString, logContext);
          }

          self.root.dynamicModal.openModal({
            dynamicModalView: isProductNotAvailable
              ? DYNAMIC_MODAL_VIEWS.ProductNotAvailable
              : DYNAMIC_MODAL_VIEWS.ErrorCart,
          });
        }
      });
    });

    const abortAddToCart = flow(function* abortAddToCart() {
      return self.activeProject?.sdkProject?.abortAddToCart().catch(error => {
        logSDKError(error, { cartId: self.cartId });
      });
    });

    const startAddingPhotosFlow = flow(function* startAddingPhotosFlow(params) {
      const { groupCode, groupId, fileIds } = params;
      const currentGroup = self.activeProject?.groups.find(g => g.id === groupId);
      const groupType = self.activeProject?.groupTypes.find(g => g.code === groupCode);
      const totalProducts = (currentGroup?.totalProducts ?? 0) + (fileIds?.length ?? 0);

      console.log('totalProducts: ', totalProducts, 'group', currentGroup, 'groupCode', self.activeProject?.groupTypes);
      if (
        isHappyCaseOrPhotoKit(groupCode) &&
        typeof groupType?.minItems === 'number' &&
        totalProducts < groupType.minItems
      ) {
        self.root.dynamicModal.openModal({
          dynamicModalView: DYNAMIC_MODAL_VIEWS.UnderMinLimitProducts,
          productLimit: groupType.minItems,
          groupWithLimit: groupType.code,
          onPressAddBox: () => {
            // Aggiungi Ora
            self.openUploadMultiSourceModal(params);
          },
          onPressRemoveProducts: () => {
            // Aggiungi dopo
            self.goToConfigurationSteps(params);
          },
        });
      } else {
        self.goToConfigurationSteps(params);
      }
    });

    /**
     * adds images to project with current product configuration
     * if no image is provided opens a picker
     */
    const addProducts = flow(function* addProducts({
      files,
      isDragged,
      isFromPersonalize,
      sourceType,
    }: {
      files?: PickedImage[];
      isDragged?: boolean;
      isFromPersonalize?: boolean;
      sourceType?: string;
    } = {}) {
      const eventToCall = isFromPersonalize
        ? AnalyticsEvents.personalize.dynamicPickerContinueClick
        : AnalyticsEvents.dynamicPicker.continueClick;
      if (!self.activeProject) return;

      if (files && files.length > 0) {
        self.root.dynamicModal.activeModal?.setIsUploadingPhoto(true);
        amplitude.trackEvent(
          eventToCall,
          eventToCall === AnalyticsEvents.personalize.dynamicPickerContinueClick
            ? {
                type: isDragged ? 'drag' : 'picker',
                n_photos: files.length,
              }
            : {
                type: isDragged ? 'drag' : 'picker',
                n_photos: files.length,
                source_type: sourceType,
                n_photos_facebook: 0,
                n_photos_gphoto: files?.filter(file => file.source === SELECTED_MENU_ITEMS.GooglePhoto)?.length ?? 0,
                n_photos_local: files?.filter(file => file.source === SELECTED_MENU_ITEMS.Computer)?.length ?? 0,
              },
        );
        braze.trackEvent(eventToCall);
        // add new products to project
        yield self.activeProject.addProducts(self.productConfigurationId, files);
      }
    });

    const addProductsV2 = flow(function* addProductsV2({
      ids,
      productConfigurationId,
      groupCode,
      groupId,
    }: {
      ids: string[];
      productConfigurationId?: string;
      groupCode?: string;
      groupId?: string;
    }) {
      const currentGroupCode = resolveGroupCodeOrThrow(self, groupCode);

      self.root.data.startPhotoPickerToFirstImageMeasure(ids.length);
      try {
        if (groupId) {
          yield self.activeProject.sdkProject.addProductsV2(
            productConfigurationId ?? self.productConfigurationId,
            ids,
            currentGroupCode,
            groupId,
          );
        } else {
          yield self.activeProject.sdkProject.addProductsV2(
            productConfigurationId ?? self.productConfigurationId,
            ids,
            currentGroupCode,
          );
        }
      } catch (error) {
        self.root.data.cleanPhotoPickerToFirstImageMeasure();
        throw error;
      }
    });

    const replaceUserImage = flow(function* replaceUserImage({
      pickedImage,
      googleAccessToken,
      productId,
      userImageId,
    }: {
      pickedImage: PickedImage;
      googleAccessToken: string | undefined;
      productId: string;
      userImageId: string;
    }) {
      if (!self.activeProject) throw new Error('No project provided');
      yield self.activeProject.sdkProject.replaceUserImage(pickedImage, googleAccessToken, productId, userImageId);
    });

    return {
      startAddingPhotosFlow,
      addToCart,
      abortAddToCart,
      goBack,
      activateEditMode,
      listenForCartEvents,
      cleanupCartEvents,
      goToMultiphotoEditorPage,
      addProducts,
      addProductsV2,
      replaceUserImage,
    };
  })
  .actions(self => {
    const loadProject = flow(function* getProject(projectId: string) {
      // retrieve project from sdk
      markBootstrapStep('project_load');
      const projectInstance = yield self.sdk.getProject(projectId); // TODO: rinominare in loadProject con le nuove versioni dell'sdk
      self.setShowUploadPhotosBanner(false);
      self.setShowSliderZoomTutorial(false);
      return projectInstance;
    });

    const createProject = flow(function* createProject(projectOptions: TProjectOptions) {
      // create project
      const thumbnailSize = (projectOptions.thumbnailSize ?? isWeb()) ? ThumbnailSize.M : ThumbnailSize.S;
      markBootstrapStep('project_create');
      const projectInstance = yield self.sdk.createProject(projectOptions.name, projectOptions.groupCode, {
        thumbnailSize,
      });

      if (projectOptions.productConfigurationId) {
        self.setProductConfigurationId(projectOptions.productConfigurationId);
      }

      if (projectOptions.groupCode) {
        self.setGroupCode(projectOptions.groupCode);
      } else {
        // preloads product data
        markBootstrapStep('project_init_product');
        yield projectInstance.initProduct(projectOptions.productConfigurationId);
      }

      return projectInstance;
    });

    return {
      loadProject,
      createProject,
    };
  })
  .actions(self => {
    /**
     * loads a project from projectId or creates a new one from options provided
     */
    const initProject = flow(function* initProject(isLogged: boolean, isNewEditor: boolean) {
      self.isNewEditor = isNewEditor;
      let projectOptions = getProjectOptions(isNewEditor);
      let projectInstance;

      self.goBack();

      try {
        // 1. try to load project from projectId
        if (typeof projectOptions.projectId !== 'undefined') {
          const projectIdToLoad = projectOptions.projectId;
          logInfo('[initProject] 1. try to load project from projectId: ', projectIdToLoad);
          projectInstance = yield retryWithBackoff(() => self.loadProject(projectIdToLoad), {
            onRetry: ({ attempt, nextDelayMs, error }) =>
              logWarning(`[initProject] loadProject retry #${attempt} in ${nextDelayMs}ms`, error),
          });
          // get last product configuration, if it is undefined it means there are no products in the project
          markBootstrapStep('project_last_configuration');
          const lastProductConfigurationId = yield projectInstance.getLastProductConfigurationId();
          if (isNewEditor) {
            // for new editor if upload route or there are no products in the loaded project
            if (isUploadRoute() || !lastProductConfigurationId) {
              self.setGroupCode(projectOptions.groupCode);
              self.setProductConfigurationId(projectOptions.productConfigurationId);
            } else {
              projectOptions.groupCode = undefined;
              projectOptions.productConfigurationId = undefined;
            }
          } else {
            // for old editor use lastProductConfigurationId if available, otherwise use default one
            projectOptions.productConfigurationId = lastProductConfigurationId ?? projectOptions.productConfigurationId;
            if (projectOptions.productConfigurationId) {
              self.setProductConfigurationId(projectOptions.productConfigurationId);
            } else {
              throw new Error(NO_PRODUCT_CONFIGURATION_ID_PROVIDED);
            }
          }
        } else {
          throw new Error(NO_PROJECT_ID_PROVIDED);
        }
      } catch (firstAttemptError) {
        try {
          const firstAttemptErrorMessage: string = extractErrorMessage(firstAttemptError);

          // if the error is not "no project id provided", log it
          if (firstAttemptErrorMessage !== NO_PROJECT_ID_PROVIDED) {
            // prevent to log Access denied error
            if (firstAttemptErrorMessage.match(/Access denied/) && !isLogged) {
              // occurs when project you are trying to access is associated with a user:
              // - and the user is not logged in
              // - or the user is logged in but the account is not the correct one to access the projectId provided in the query string
              // occurs when the project is no longer available because it was closed after being added to the cart
              logWarning('[initProject] Redirect to Login Page: ', firstAttemptError);
              // stop execution and redirect to login scren
              goToLoginScreen();
              return projectOptions;
            } else if (
              firstAttemptErrorMessage.match(/400 Bad Request/) ||
              firstAttemptErrorMessage.match(/not found either locally or remotely/) ||
              firstAttemptErrorMessage.match(/cannot find product/) ||
              firstAttemptErrorMessage.match(/Access denied/)
            ) {
              // occurs when an incorrect projectId is present in the query string or project is no longer available
              logWarning('[initProject] Warning loading project: ', firstAttemptError);
            } else {
              logError('[initProject] Error loading project: ', firstAttemptError);
            }
          }

          // remove projectId from options to force creation of a new project
          projectOptions.projectId = undefined;
          // 2. try to create project from options (with retry on transient network errors)
          logInfo('[initProject] 2. try to create project from options: ', projectOptions);
          projectInstance = yield retryWithBackoff(() => self.createProject(projectOptions), {
            // Wider budget than the 3/500ms default: this is the step that decides
            // whether the editor starts at all. The default spends its three
            // attempts inside ~1.5-2.5s, which on a 3g-class link (rtt ~850ms,
            // downlink ~0) can elapse while the network is merely slow rather than
            // dead — see PHOTOSI-STORE-HUB-234. 4 attempts at 1s base spread the
            // retries over ~7-10s instead. The first attempt is still immediate,
            // so a healthy network pays nothing.
            maxAttempts: 4,
            baseDelayMs: 1000,
            onRetry: ({ attempt, nextDelayMs, error }) =>
              logWarning(`[initProject] createProject retry #${attempt} in ${nextDelayMs}ms`, error),
          });
        } catch (secondAttemptError) {
          try {
            logError(
              '[initProject] Project creation failed, first error',
              firstAttemptError,
              'second error',
              secondAttemptError,
            );

            // 3. if project creation fails, try to create the project with default configurations
            const projectOptions = getDefaultProjectOptions(isNewEditor);
            logInfo('[initProject] 3. Try to create the project with default configurations ', projectOptions);
            markBootstrapStep('project_create_fallback');
            // Deliberately left on the default 3/500ms budget. This step differs
            // from step 2 only in the project OPTIONS, not in the transport, so it
            // is a fallback for a bad config, not for a bad network: if step 2 just
            // exhausted a ~10s budget on network errors, widening this one too
            // would mostly add spinner time before the error the user is going to
            // get anyway (~20s instead of ~12s to first feedback).
            projectInstance = yield retryWithBackoff(() => self.createProject(projectOptions), {
              onRetry: ({ attempt, nextDelayMs, error }) =>
                logWarning(`[initProject] fallback createProject retry #${attempt} in ${nextDelayMs}ms`, error),
            });
          } catch (thirdAttemptError) {
            logError(
              '[initProject] Project creation failed with default options, first error',
              firstAttemptError,
              'second error',
              secondAttemptError,
              'third error',
              thirdAttemptError,
            );
            reportBootstrapFailure(thirdAttemptError);
          }
        }
      }

      if (!projectInstance) {
        // All three attempts failed (the innermost catch above only logs, it doesn't
        // rethrow) — bail out here instead of crashing on projectInstance below.
        return projectOptions;
      }

      projectInstance.startShareImageNotificationPolling(45000);

      // save the sdkProject instance (important)
      markBootstrapStep('project_get_id');
      const projectId = yield projectInstance.getId(); // TODO: remove this row after sdk fix with the same id as qs
      sdkProjects.set(projectId, projectInstance);

      logInfo('[initProject] COMPLETED', { ...projectOptions, projectId });

      return { ...projectOptions, projectId };
    });

    const removeProduct = flow(function* removeProduct(productId: string) {
      if (!self.removingProduct) {
        // recupero il prodotto tramite il productId oppure prendo quello già attivo
        const product = productId
          ? self.activeProject?.products.find(product => product.id === productId)
          : self.activeProduct;

        if (!product) throw new EditorError('Product not found');
        // removing toast for those products that have a quality alert
        if (product?.userImages?.[0]?.qualityAlert) {
          self.root.toastStore.removeToast(`alert-quality-image-toast-${product.userImages[0].id}`);
        }
        self.removingProduct = product;
      } else {
        throw new EditorError(`Waiting product removal for ${self.removingProduct?.id}`);
      }
    });

    const completeRemoveProduct = flow(function* completeRemoveProduct() {
      if (self.removingProduct) {
        const temp = self.removingProduct;
        self.removingProduct = undefined;

        try {
          Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.frame_removal_time);
          yield temp?.remove(true);
          Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.frame_removal_time);
        } catch (error) {
          self.removingProduct = temp;
          Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.frame_removal_time);
          throw error;
        }
      }
    });

    const selectUserImage = flow(function* selectUserImage(
      userImage,
      autosave = true,
      fromZoom = false,
      fromOptions = false,
    ) {
      // Guard: product may have been deleted between the tap and this async execution
      if (!isAlive(userImage)) return;
      if (self.activeProject?.sdkProject && !!userImage && self.isProductSelectionEnabled) {
        self.disableInteractions();
        try {
          amplitude.trackEvent(AnalyticsEvents.personalize.editImageClick, {
            ...parseAnalyticsParamsByUserImage(userImage),
            source: fromZoom ? 'zoom' : 'personalize',
            section_name: fromOptions
              ? SECTION_NAMES.OPTIONS_MENU
              : fromZoom
                ? SECTION_NAMES.ZOOM
                : SECTION_NAMES.PERSONALIZE,
          });
          self.setActiveProduct(userImage.productId);
          self.setActiveUserImageArea(userImage.userImageAreaId);
          yield userImage.select(autosave); // loads imageEditor
          if (!self.isEditModeActive) {
            yield self.activateEditMode(ACTIVE_TOOLS.frame, autosave);
          }
        } catch (error) {
          logError('[selectUserImage]', error, JSON.stringify(userImage));
        }
        self.enableInteractions();
      }
    });

    const selectUserImageV3 = flow(function* selectUserImageV3(userImage, autosave = true) {
      if (!self.activeProject?.sdkProject || !userImage || !self.isProductSelectionEnabled) return;

      const token = Symbol();
      self.setSelectUserImageToken(token);
      const isCancelled = () => self.selectUserImageToken !== token;

      self.disableInteractions();
      try {
        self.setActiveProduct(userImage.productId);
        self.setActiveUserImageArea(userImage.userImageAreaId);
        yield userImage.select(autosave); // loads imageEditor

        if (isCancelled()) {
          yield userImage.unloadEditor();
          return;
        }

        if (!self.isEditModeActive) {
          yield self.activateEditMode(ACTIVE_TOOLS.frame, autosave);
        }
      } catch (error) {
        if (!isCancelled()) logError('[selectUserImage]', error, JSON.stringify(userImage));
      } finally {
        self.enableInteractions();
      }
    });

    const switchToGroup = (
      options:
        | {
            groupId: string;
            scrollTo: 'top' | 'bottom' | number | IProductModel;
            forceOpening?: boolean;
          }
        | undefined,
    ) => {
      if (!options) {
        self.switchToGroupHelper = undefined;
        return;
      }

      self.switchToGroupHelper = {
        groupId: options.groupId,
        scrollTo: options.scrollTo,
        forceOpening: options.forceOpening,
      };

      if (options.groupId && self.activeGroup?.id !== options.groupId) {
        self.setActiveGroupId(options.groupId);
      }
    };

    const createShareTokenMobile = flow(function* createShareTokenMobile() {
      if (!self.activeProject) throw new Error('No project provided');
      const token = yield self.activeProject.sdkProject.createShareTokenMobile();
      self.setShareTokenMobile(token);
      return token;
    });

    const createShareTokenFamilyAndFriends = flow(function* createShareTokenFamilyAndFriends() {
      if (!self.activeProject) throw new Error('No project provided');
      const token = yield self.activeProject.sdkProject.createShareTokenFamilyAndFriends();
      self.setShareTokenFamilyAndFriends(token);
      return token;
    });

    const getSharedProjectSettings = flow(function* getSharedProjectSettings(shareToken: string) {
      if (!self.sdk) throw new Error('No SDK provided');
      const projectSettings = yield self.sdk.getSharedProjectSettings(shareToken);
      return projectSettings;
    });

    const addSharedPhotos = flow(function* addSharedPhotos({
      shareToken,
      images,
      projectId,
      thumbnailSize,
    }: {
      shareToken: string;
      images: PickedImage[];
      projectId: string;
      thumbnailSize: string;
    }) {
      if (!self.sdk) throw new Error('No SDK provided');
      yield self.sdk.addSharedPhotos(shareToken, images, projectId, thumbnailSize);
    });

    const startShareObserver = flow(function* startShareObserver() {
      if (!self.activeProject) throw new Error('No project provided');
      yield self.activeProject.sdkProject.startShareObserver();
    });

    const stopShareObserver = () => {
      if (!self.activeProject) throw new Error('No project provided');
      if (!self.activeProject.sdkProject) return;
      self.activeProject.sdkProject.stopShareObserver();
    };

    const startShareImageNotificationPolling = () => {
      if (!self.activeProject) throw new Error('No project provided');
      self.activeProject.sdkProject.startShareImageNotificationPolling(45000);
    };

    const getMinDiscountScales = flow(function* getMinDiscountScales(
      groupCode: string,
      productConfigurationId?: string,
    ) {
      let discount = [];
      try {
        if (!self.activeProject) {
          throw new Error('No project provided');
        } else if (!groupCode) {
          throw new Error('No groupCode provided');
        } else {
          discount = yield self.activeProject.sdkProject.getMinDiscountScales(groupCode, productConfigurationId);
        }
      } catch (error) {
        logError('[getMinDiscountScales]', error, JSON.stringify({ groupCode, productConfigurationId }));
      }

      return discount;
    });

    // Returns the quantity of the whole band (products sharing the same price list) used by the discount scales,
    // or undefined if it can't be computed
    const getDiscountQuantity = flow(function* getDiscountQuantity(groupCode: string, productConfigurationId: string) {
      let quantity: number | undefined;
      try {
        if (!self.activeProject) {
          throw new Error('No project provided');
        } else if (!groupCode) {
          throw new Error('No groupCode provided');
        } else {
          quantity = yield self.activeProject.sdkProject.getDiscountQuantity(groupCode, productConfigurationId);
        }
      } catch (error) {
        logError('[getDiscountQuantity]', error, JSON.stringify({ groupCode, productConfigurationId }));
      }

      return quantity;
    });

    const getFrameConfigStepsData = flow(function* getFrameConfigStepsData(productConfigurationId: string) {
      if (!self.activeProject) {
        throw new Error('No project provided');
      } else {
        return self.activeProject.getFrameConfigStepsData(productConfigurationId);
      }
    });

    const startAddingPhotosFlowV3 = (
      params: any,
      navigation: any,
      filteredParams: any,
      callback?: () => void,
      fromGrid: boolean = false,
    ) => {
      const { groupCode, groupId, images, initialProductConfigurationId, googleAccessToken } = params;
      const currentGroup = self.activeProject?.groups.find(g => g.id === groupId);
      const groupType = self.activeProject?.groupTypes.find(g => g.code === groupCode);
      const totalProducts = (currentGroup?.totalProducts ?? 0) + (images?.length ?? 0);
      self.setShowUploadPhotosBanner(false);
      self.setUploading(true);
      currentGroup?.checkMinMaxProductsAndNotifyV3(totalProducts);
      addProductsV3({
        images: images,
        productConfigurationId: initialProductConfigurationId,
        groupCode,
        groupId,
        googleAccessToken,
      });
      !fromGrid && navigationResetTo(navigation, ROUTE_NAMES.EditorNew, { ...filteredParams });
      callback && callback();
      if (
        isHappyCaseOrPhotoKit(groupCode) &&
        typeof groupType?.minItems === 'number' &&
        totalProducts < groupType.minItems
      ) {
        self.root.dynamicModal.openModal({
          dynamicModalView: DYNAMIC_MODAL_VIEWS.UnderMinLimitProducts,
          productLimit: groupType.minItems,
          groupWithLimit: groupType.code,
          onPressAddBox: () => {
            // Aggiungi Ora
            self.root.dynamicModal.closeModal();
            navigateTo(navigation, ROUTE_NAMES.Upload, {
              groupCodeExternal: groupCode,
              skipConfigurationStep: STEPS.BoxStyle,
              skipStepValue: initialProductConfigurationId,
              ...filteredParams,
            });
          },
          onPressRemoveProducts: () => {
            // Aggiungi dopo
            self.root.dynamicModal.closeModal();
          },
        });
      }
    };

    const addProductsV3 = flow(function* addProductsV3({
      images,
      productConfigurationId,
      groupCode,
      groupId,
      googleAccessToken,
    }: {
      images: PickedImage[];
      productConfigurationId?: string;
      groupCode?: string;
      groupId?: string;
      googleAccessToken?: string;
    }) {
      const currentGroupCode = resolveGroupCodeOrThrow(self, groupCode);
      self.root.data.startPhotoPickerToFirstImageMeasure(images.length);
      try {
        yield self.activeProject.sdkProject.addProductsV3(
          productConfigurationId ?? self.productConfigurationId,
          images,
          currentGroupCode,
          groupId,
          googleAccessToken,
        );
      } catch (error) {
        self.root.data.cleanPhotoPickerToFirstImageMeasure();
        throw error;
      }
    });

    const setFromSmartphoneOrFamilyAndFriends = (data: boolean) => {
      self.setShowUploadPhotosBanner(false);
      self.isFromSmartphoneORFamilyAndFriends = data;
    };

    const setScrollOffset = (offset: number) => {
      self.scrollOffset = offset;
    };

    const setPersonalizedEventSent = (value: boolean) => {
      self.personalizedEventSent = value;
    };

    return {
      initProject,
      removeProduct,
      completeRemoveProduct,
      selectUserImage,
      selectUserImageV3,
      switchToGroup,
      createShareTokenMobile,
      createShareTokenFamilyAndFriends,
      getSharedProjectSettings,
      addSharedPhotos,
      startShareObserver,
      stopShareObserver,
      startShareImageNotificationPolling,
      getMinDiscountScales,
      getDiscountQuantity,
      startAddingPhotosFlowV3,
      setFromSmartphoneOrFamilyAndFriends,
      setScrollOffset,
      addProductsV3,
      setPersonalizedEventSent,
      getFrameConfigStepsData,
    };
  });
